/**
 * Zod schemas + inferred types for every backend response the portal consumes
 * (spec §10). Every HTTP body is parsed through these before use (spec §8.4).
 *
 * Success bodies use the common envelope `{ data, requestId }` (spec §10.1);
 * `httpClient` unwraps `data`. The schemas below describe the inner `data`.
 */
import { z } from 'zod';

export const PROVIDERS = ['google', 'linkedin', 'facebook'] as const;
export const providerIdSchema = z.enum(PROVIDERS);
export type ProviderId = z.infer<typeof providerIdSchema>;

export const accountRoleSchema = z.enum(['student', 'teacher', 'parent', 'other']);
export type AccountRole = z.infer<typeof accountRoleSchema>;

export const userAccountStatusSchema = z.enum([
  'ACTIVE',
  'PENDING_ONBOARDING',
  'SUSPENDED',
  'PENDING_REVIEW',
]);
export type UserAccountStatus = z.infer<typeof userAccountStatusSchema>;

// -- Error envelope (spec §10.1) --------------------------------------------
export const apiErrorSchema = z.object({
  error: z.object({
    code: z.string(),
    message: z.string(),
    details: z.record(z.union([z.string(), z.number(), z.boolean()])).optional(),
  }),
  requestId: z.string(),
});
export type ApiErrorBody = z.infer<typeof apiErrorSchema>;

/** Builds an `ApiSuccess<T>` envelope schema around an inner data schema. */
export function envelope<T extends z.ZodTypeAny>(data: T) {
  return z.object({ data, requestId: z.string() });
}

/** Validates the success envelope shape without constraining the inner payload. */
export const envelopeShapeSchema = z.object({
  data: z.unknown(),
  requestId: z.string(),
});

// -- Providers (spec §10.2) -------------------------------------------------
export const providerViewSchema = z.object({
  id: providerIdSchema,
  label: z.string(),
  enabled: z.boolean(),
  priority: z.number(),
  supportsLogin: z.boolean(),
  supportsLink: z.boolean(),
});
export type ProviderView = z.infer<typeof providerViewSchema>;

export const providersResponseSchema = z.object({
  providers: z.array(providerViewSchema),
});
export type ProvidersResponse = z.infer<typeof providersResponseSchema>;

// -- Session (spec §10.3) ---------------------------------------------------
export const sessionUserSchema = z.object({
  id: z.string(),
  displayName: z.string().nullable(),
  email: z.string().nullable(),
  emailVerified: z.boolean(),
  pictureUrl: z.string().nullable(),
  accountRole: accountRoleSchema.nullable(),
  status: userAccountStatusSchema,
  onboardingCompleted: z.boolean(),
});
export type SessionUser = z.infer<typeof sessionUserSchema>;

export const anonymousSessionSchema = z.object({ authenticated: z.literal(false) });

export const authenticatedSessionSchema = z.object({
  authenticated: z.literal(true),
  csrfToken: z.string(),
  user: sessionUserSchema,
  nextUrl: z.string().nullable(),
});

export const sessionResponseSchema = z.discriminatedUnion('authenticated', [
  anonymousSessionSchema,
  authenticatedSessionSchema,
]);
export type SessionResponse = z.infer<typeof sessionResponseSchema>;
export type AuthenticatedSession = z.infer<typeof authenticatedSessionSchema>;

// -- Card visit (user-detail.md, Nhóm D) ------------------------------------
export const cardFieldSchema = z.object({ value: z.string(), visible: z.boolean() });
export const cardSocialSchema = z.object({
  platform: z.string(),
  url: z.string(),
  visible: z.boolean(),
});
export const cardVisitSchema = z.object({
  email: cardFieldSchema.optional(),
  phone: cardFieldSchema.optional(),
  website: cardFieldSchema.optional(),
  linkedin: cardFieldSchema.optional(),
  address: cardFieldSchema.optional(),
  bio: cardFieldSchema.optional(),
  socials: z.array(cardSocialSchema).optional(),
});

// -- User profile (spec §10.4 + user-detail.md) -----------------------------
export const userProfileSchema = z.object({
  id: z.string(),
  displayName: z.string().nullable(),
  email: z.string().nullable(),
  emailVerified: z.boolean(),
  pictureUrl: z.string().nullable(),
  accountRole: accountRoleSchema.nullable(),
  status: userAccountStatusSchema,
  createdAt: z.string(),
  updatedAt: z.string(),
  legal: z.object({
    termsAcceptedVersion: z.string().nullable(),
    privacyAcceptedAt: z.string().nullable(),
  }),
  // Rich profile (Nhóm A/B/C) — optional so older backends still parse.
  initials: z.string().nullable().optional(),
  title: z.string().nullable().optional(),
  company: z.string().nullable().optional(),
  tagline: z.string().nullable().optional(),
  domain: z.string().nullable().optional(),
  yearsExperience: z.number().nullable().optional(),
  midStatLabel: z.string().nullable().optional(),
  midStatValue: z.number().nullable().optional(),
  card: cardVisitSchema.optional(),
  cardPublic: z.boolean().optional(),
  cardHandle: z.string().nullable().optional(),
});
export type UserProfile = z.infer<typeof userProfileSchema>;

// -- Public card (GET /api/public/users/{handle}) ---------------------------
export const publicCardSchema = z.object({
  handle: z.string().nullable(),
  name: z.string().nullable(),
  initials: z.string().nullable(),
  photo: z.string().nullable(),
  title: z.string().nullable(),
  company: z.string().nullable(),
  tagline: z.string().nullable(),
  domain: z.string().nullable(),
  stats: z.array(z.object({ label: z.string(), value: z.string() })),
  card: z.object({
    email: z.string().optional(),
    phone: z.string().optional(),
    website: z.string().optional(),
    linkedin: z.string().optional(),
    address: z.string().optional(),
    bio: z.string().optional(),
    socials: z.array(z.object({ platform: z.string(), url: z.string() })).optional(),
  }),
});
export type PublicCardResponse = z.infer<typeof publicCardSchema>;

// -- Onboarding (spec §10.5) ------------------------------------------------
export const completeOnboardingResponseSchema = z.object({
  user: userProfileSchema,
  nextUrl: z.string().nullable(),
});
export type CompleteOnboardingResponse = z.infer<typeof completeOnboardingResponseSchema>;

// -- Identities (spec §10.7) ------------------------------------------------
export const connectedIdentitySchema = z.object({
  provider: providerIdSchema,
  linkedAt: z.string(),
  lastAuthenticatedAt: z.string().nullable(),
  emailMasked: z.string().nullable(),
  canUnlink: z.boolean(),
  unlinkDisabledReason: z.enum(['LAST_AUTH_METHOD', 'POLICY']).nullable(),
});
export type ConnectedIdentity = z.infer<typeof connectedIdentitySchema>;

export const identitiesResponseSchema = z.object({
  identities: z.array(connectedIdentitySchema),
  availableToLink: z.array(providerViewSchema),
});
export type IdentitiesResponse = z.infer<typeof identitiesResponseSchema>;

// -- Link start (spec §10.9, Option B) --------------------------------------
export const linkStartResponseSchema = z.object({
  redirectUrl: z.string(),
});
export type LinkStartResponse = z.infer<typeof linkStartResponseSchema>;

// -- Logout all (spec §10.12) -----------------------------------------------
export const logoutAllResponseSchema = z.object({
  revokedSessionCount: z.number(),
});
export type LogoutAllResponse = z.infer<typeof logoutAllResponseSchema>;

// -- Request bodies ---------------------------------------------------------
export type CompleteOnboardingRequest = {
  displayName: string;
  accountRole: AccountRole;
  termsVersion: string;
  privacyAccepted: true;
};

/** Partial profile update. The profile form and card form PATCH independent subsets. */
export type UpdateProfileRequest = {
  displayName?: string;
  accountRole?: AccountRole;
  pictureUrl?: string;
  initials?: string;
  title?: string;
  company?: string;
  tagline?: string;
  domain?: string;
  yearsExperience?: number | null;
  midStatLabel?: string;
  midStatValue?: number | null;
  card?: CardVisitRequest;
  cardPublic?: boolean;
  cardHandle?: string;
};

export type CardVisitRequest = z.infer<typeof cardVisitSchema>;

export type LinkStartRequest = {
  client?: string;
  returnRoute?: '/account/connections';
};
